import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Loading from 'components/Loading';
import CategoryForm from '../components/CategoryForm';

// GraphQL query to get category
const GET_ANNUAL_TYPE = gql`
  query GetAnnualType($id: Int) {
    getAnnualType(id: $id) {
      id
      name
      account {
        id
        business_dba
      }
      logo_id
      logo
      type
      code
      code_date
      code_time
    }
  }
`;

// GraphQL mutation for updating category
const UPDATE_ANNUAL_TYPE = gql`
  mutation UpdateAnnualType($input: UpdateAnnualTypeInput!) {
    updateAnnualType(input: $input) {
      id
      name
      account {
        id
        business_dba
      }
    }
  }
`;

const EditCategoryContent = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [saving, setSaving] = useState(false);

  // Get account_id from URL params
  const searchParams = new URLSearchParams(location.search);
  const accountId = searchParams.get('account_id');

  // Determine route prefix for navigation
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Fetch category data
  const { data, loading, error } = useQuery(GET_ANNUAL_TYPE, {
    variables: { id: parseInt(id) },
    fetchPolicy: 'network-only',
    skip: !id,
  });

  // Mutation
  const [updateAnnualType] = useMutation(UPDATE_ANNUAL_TYPE);

  const handleSubmit = async (formData) => {
    setSaving(true);

    try {
      const { data: responseData } = await updateAnnualType({
        variables: {
          input: {
            id: parseInt(id),
            ...formData,
          },
        },
      });

      if (responseData?.updateAnnualType) {
        // Navigate back to categories list with success state
        const redirectUrl = accountId
          ? `${routePrefix}/annualcategories?account_id=${accountId}`
          : `${routePrefix}/annualcategories`;

        history.push(redirectUrl, {
          toaster: {
            type: 'success',
            title: 'Success!',
            message: 'Category updated successfully.',
          },
        });
      }
    } catch (err) {
      setSaving(false);
      throw err;
    }
  };

  const handleCancel = () => {
    const backUrl = accountId
      ? `${routePrefix}/annualcategories?account_id=${accountId}`
      : `${routePrefix}/annualcategories`;
    history.push(backUrl);
  };

  if (loading) {
    return (
      <div style={{ padding: '100px', textAlign: 'center' }}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '100px', textAlign: 'center', color: '#ef4444' }}>
        Error loading category: {error.message}
      </div>
    );
  }

  return (
    <CategoryForm
      mode="edit"
      initialData={data?.getAnnualType}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      saving={saving}
      accountId={accountId}
    />
  );
};

// Wrap with theme provider
const EditCategory = () => (
  <EnhancedThemeProvider>
    <EditCategoryContent />
  </EnhancedThemeProvider>
);

export default EditCategory;
